import { useRef, useEffect, useState } from "react";
import {
  AdminVideoCard,
  VideoForm,
  SubtitleProgressBackdrop,
  DocumentAssociationDialog,
  PopUpMessageComponent,
} from "../../../component/molecule";
import { Box, Grid, CircularProgress, Typography } from "@mui/material";
import HeadingComponent from "../../../component/atom/Headings/Heading";
import DialogComponent from "../../../component/atom/Dialog/Dialog";
import SearchBarComponent from "../../../component/atom/Inputs/SearchBar";
import TextIconButtonComponent from "../../../component/atom/Buttons/TextIconButton";
import { faVideo } from "@fortawesome/free-solid-svg-icons";
import { Actions } from "../../../core/modules/Actions";
import { connect, useSelector } from "react-redux";
import usePolling from "../../../hook/usePolling";
import _ from "lodash";
import { ASSOCIATION_ACTION } from "../../../core/Constant";

const PER_PAGE = 12;

const VideoScreen = ({
  uploadedVideoList,
  fetchVideo,
  loadingAction,
  verifyData,
  fetchSubtitleStatus,
  checkVideoAssociation,
  setRegeneratingVideoId,
}) => {
  const regeneratingVideoId = useSelector((state) => state.video.get("regeneratingVideoId"));
  const subtitleStatus = useSelector((state) => state.video.get("subtitleStatus"));
  const videoAssociation = useSelector((state) => state.video.get("videoAssociation"));
  const associateContent = (() => {
    const contents = videoAssociation?.associate_items?.courseContents;
    if (Array.isArray(contents)) return contents;
    if (contents && typeof contents === "object") return Object.values(contents);
    return [];
  })();

  const [videoUpload, setUploadVideo] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [uploadedVideos, setUploadedVideos] = useState([]);
  const [cloudfronturl, setCloduFrontUrl] = useState();
  const [searchValue, setSearchValue] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [pendingVideoToEdit, setPendingVideoToEdit] = useState(null);
  const [checkingAssociationId, setCheckingAssociationId] = useState(null);
  const [errorOpen, setErrorOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [stopPolling, setStopPolling] = useState(false);

  useEffect(() => {
    if (regeneratingVideoId) {
      setStopPolling(false);
      setErrorOpen(false);
      setSuccessOpen(false);
    }
  }, [regeneratingVideoId]);

  const isPollingActive = Boolean(regeneratingVideoId) && !stopPolling && !errorOpen;

  const progressPercent = (() => {
    if (!isPollingActive || !subtitleStatus) return null;
    const completed = subtitleStatus?.completed_items ?? subtitleStatus?.data?.completed_items;
    const total = subtitleStatus?.total_items ?? subtitleStatus?.data?.total_items;
    if (total !== undefined && total > 0 && completed !== undefined) {
      return Math.round((completed / total) * 100);
    }
    return null;
  })();

  usePolling(
    () => {
      if (!isPollingActive) return;
      return fetchSubtitleStatus(regeneratingVideoId)
        .then((res) => {
          if (res && res.error) {
            setStopPolling(true);
            setRegeneratingVideoId(null);
            setErrorOpen(true);
            return;
          }
          const status = res?.data?.status || res?.status;
          const statusStr = typeof status === "string" ? status.toLowerCase() : "";
          if (statusStr === "failed" || statusStr === "error" || statusStr === "cancelled") {
            setStopPolling(true);
            setRegeneratingVideoId(null);
            setErrorOpen(true);
          } else if (statusStr === "completed") {
            setStopPolling(true);
            setRegeneratingVideoId(null);
            setSuccessOpen(true);
          }
        })
        .catch((err) => {
          console.error("Subtitle polling failed:", err);
          setStopPolling(true);
          setRegeneratingVideoId(null);
          setErrorOpen(true);
        });
    },
    isPollingActive ? 5000 : null,
    [regeneratingVideoId, isPollingActive],
    true
  );

  const isFetchingRef = useRef(false);
  const hasMoreRef = useRef(true);
  const pageRef = useRef(1);
  const searchRef = useRef("");
  const initialFetchDone = useRef(false);
  const searchTimerRef = useRef(null);

  const fetchVideos = async (pageNum = 1, searchQuery = "") => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    setIsLoading(true);

    try {
      const params = {
        page: pageNum,
        per_page: PER_PAGE,
        ...(searchQuery && searchQuery.trim() ? { search: searchQuery.trim() } : {}),
      };
      await fetchVideo(params);
    } catch (err) {
      console.error(err);
    } finally {
      isFetchingRef.current = false;
      setIsLoading(false);
    }
  };

  // Initial fetch on mount (fires exactly once)
  useEffect(() => {
    if (!initialFetchDone.current) {
      initialFetchDone.current = true;
      fetchVideos(1, "");
    }
  }, []);

  // Debounced search fetch (cancels any pending search timer and resets to page 1)
  useEffect(() => {
    if (!initialFetchDone.current) return;

    searchRef.current = searchValue;
    pageRef.current = 1;
    hasMoreRef.current = true;

    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    searchTimerRef.current = setTimeout(() => {
      fetchVideos(1, searchValue);
    }, 400);

    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
  }, [searchValue]);

  // Increase page number on page scroll (infinite scroll)
  useEffect(() => {
    const handleScroll = () => {
      if (isFetchingRef.current || !hasMoreRef.current) return;

      const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
      const windowHeight = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight || 0;
      const documentHeight = document.documentElement.scrollHeight || document.body.scrollHeight || 0;

      if (scrollTop + windowHeight >= documentHeight - 200) {
        const nextPage = pageRef.current + 1;
        pageRef.current = nextPage;
        fetchVideos(nextPage, searchRef.current);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Sync uploaded videos and determine if more pages exist
  useEffect(() => {
    if (uploadedVideoList) {
      const list = Array.isArray(uploadedVideoList)
        ? uploadedVideoList
        : Array.isArray(uploadedVideoList?.data)
          ? uploadedVideoList.data
          : [];
      setUploadedVideos(list);

      const total = uploadedVideoList?.total;
      const lastPage = uploadedVideoList?.last_page;
      const currentPage = uploadedVideoList?.current_page || pageRef.current;

      if (lastPage !== undefined && currentPage !== undefined) {
        hasMoreRef.current = currentPage < lastPage;
      } else if (total !== undefined) {
        hasMoreRef.current = list.length < total;
      } else {
        hasMoreRef.current = list.length >= PER_PAGE * pageRef.current;
      }
    }
  }, [uploadedVideoList]);

  useEffect(() => {
    setCloduFrontUrl(_.get(verifyData, "video_url"));
  }, [verifyData]);

  useEffect(() => {
    const { action, loading } = loadingAction || {};
    const type = action?.type;
    if (type === "UPLOAD_VIDEO" && !loading) {
      setUploadVideo(loading);
      setSelectedVideo(null);
      pageRef.current = 1;
      hasMoreRef.current = true;
      fetchVideos(1, searchRef.current);
    }
  }, [loadingAction]);

  const handleOpenUpload = () => {
    setSelectedVideo(null);
    setUploadVideo(true);
  };

  const handleEditVideo = async (video) => {
    const videoId = video.id || video.video_id;
    if (checkingAssociationId) return;
    setCheckingAssociationId(videoId);
    try {
      const response = await checkVideoAssociation(videoId);
      const payload = response?.data;
      const contentList = payload?.associate_items?.courseContents;
      const isAssociated = payload?.is_associated || (
        Array.isArray(contentList)
          ? contentList.length > 0
          : (contentList && typeof contentList === "object" ? Object.keys(contentList).length > 0 : false)
      );
      if (isAssociated) {
        setPendingVideoToEdit(video);
        setSaveConfirmOpen(true);
      } else {
        setSelectedVideo(video);
        setUploadVideo(true);
      }
    } catch (error) {
      console.error("Error checking video association:", error);
      setSelectedVideo(video);
      setUploadVideo(true);
    } finally {
      setCheckingAssociationId(null);
    }
  };

  const onConfirmGlobalSave = () => {
    setSaveConfirmOpen(false);
    if (pendingVideoToEdit) {
      setSelectedVideo(pendingVideoToEdit);
      setUploadVideo(true);
      setPendingVideoToEdit(null);
    }
  };

  const onCancelGlobalSave = () => {
    setSaveConfirmOpen(false);
    setPendingVideoToEdit(null);
  };

  const handleCloseModal = () => {
    setUploadVideo(false);
    setSelectedVideo(null);
  };

  const renderUploadedVideos = Array.isArray(uploadedVideos) ? uploadedVideos.map((item, index) => {

    return (
      <Grid item xs={12} sm={6} md={4} key={item.id}>
        <AdminVideoCard
          key={item?.id}
          id={item?.id}
          description={item?.title}
          thumbnail={item?.thumbnail}
          btnText={"Copy Link"}
          videoUrl={`${cloudfronturl}${item?.aws_file_name}`}
          subtitle_tracks={item?.tracks}
          index={index}
          onEdit={(videoData) => handleEditVideo(videoData || item)}
          loadingEdit={checkingAssociationId === item.id}
        />
      </Grid>
    );
  }) : null;

  return (
    <>
      <Box
        className="main-screen-container"
        sx={{
          ...(regeneratingVideoId && {
            userSelect: "none",
          }),
        }}
      >
        <Grid container direction="row" justifyContent="space-between" alignItems={"center"} mb={4} gap={2}>
          <Grid item>
            <HeadingComponent
              text={"Video"}
              fontweigth={600}
              size={40}
              fontfamily={"Montserrat"}
            />
          </Grid>
          <Grid item>
            <Grid container alignItems="center" gap={2}>
              <Grid item>
                <SearchBarComponent
                  value={searchValue}
                  disabled={Boolean(regeneratingVideoId)}
                  onchange={(e) => !regeneratingVideoId && setSearchValue(e.target.value)}
                />
              </Grid>
              <Grid item>
                <TextIconButtonComponent
                  btnText={"Upload Video"}
                  icon={faVideo}
                  disabled={Boolean(regeneratingVideoId)}
                  onclick={() => !regeneratingVideoId && handleOpenUpload()}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* Initial / Search Loading State */}
        {isLoading && uploadedVideos.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              py: 12,
            }}
          >
            <CircularProgress sx={{ color: "#9834F0" }} size={48} />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {renderUploadedVideos}
          </Grid>
        )}

        {/* Empty State */}
        {!isLoading && uploadedVideos.length === 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              py: 8,
            }}
          >
            <Typography sx={{ color: "#64748B", fontSize: "16px", fontWeight: 500 }}>
              {searchValue ? "No videos found matching your search." : "No uploaded videos found."}
            </Typography>
          </Box>
        )}

        {/* Infinite Scroll Loader at bottom */}
        {isLoading && uploadedVideos.length > 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              py: 4,
            }}
          >
            <CircularProgress sx={{ color: "#9834F0" }} size={36} />
          </Box>
        )}
      </Box>
      {saveConfirmOpen && associateContent.length > 0 && (
        <DocumentAssociationDialog
          association={{
            data: videoAssociation?.associate_items,
            action: ASSOCIATION_ACTION.EDIT,
            entityType: "video",
            entityName: pendingVideoToEdit?.title || pendingVideoToEdit?.name,
          }}
          onConfirm={onConfirmGlobalSave}
          onClose={onCancelGlobalSave}
        />
      )}
      <DialogComponent
        title={selectedVideo ? "Update Video" : "Upload Video"}
        open={videoUpload}
        onClose={handleCloseModal}
      >
        <VideoForm
          isClsose={videoUpload}
          videoData={selectedVideo}
          onClose={handleCloseModal}
        />
      </DialogComponent>

      <SubtitleProgressBackdrop
        open={isPollingActive}
        completedItems={subtitleStatus?.completed_items}
        totalItems={subtitleStatus?.total_items}
        progressPercent={progressPercent}
      />

      <PopUpMessageComponent
        open={errorOpen}
        type="error"
        message="An error occurred while generating subtitles for your video. Please try again."
        btntext="Close"
        onclick={() => setErrorOpen(false)}
        onclose={() => setErrorOpen(false)}
      />

      <PopUpMessageComponent
        open={successOpen}
        type="success"
        message="Subtitles have been successfully generated for your video."
        btntext="OK"
        onclick={() => setSuccessOpen(false)}
        onclose={() => setSuccessOpen(false)}
      />
    </>
  );
};

export default connect(
  (state) => ({
    uploadedVideoList: state.video.get("uploadedVideoList"),
    popupClose: state.video.get("popupClose"),
    loadingAction: state.common.get("loadingAction"),
    verifyData: state.auth.get("verifyData"),
  }),
  {
    uploadVideo: Actions.video.uploadVideo,
    fetchVideo: Actions.video.fetchVideo,
    verifyToken: Actions.auth.verifyToken,
    fetchSubtitleStatus: Actions.video.fetchSubtitleStatus,
    checkVideoAssociation: Actions.video.checkVideoAssociation,
    setRegeneratingVideoId: Actions.video.setRegeneratingVideoId,
  }
)(VideoScreen);
